// app.config.ts (Corrected and Fully Modernized for v20)

import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, isDevMode, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideServiceWorker } from '@angular/service-worker';

// --- PrimeNG ---
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import Aura from "@primeng/themes/aura"; // Switched back to Aura as requested

// --- Firebase (Modern Standalone API) ---
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideDatabase, getDatabase } from '@angular/fire/database';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';

// Note: Add other Firebase services like provideFirestore, provideFunctions as needed

// --- App Specific ---
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { CustomerService } from './customer/customer.service';
import { BaseMasterDataService } from './services/base-master-data.service';
import { authInterceptor } from './helpers/auth.interceptor';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { StaleBuildErrorHandler } from './helpers/stale-build-recovery';
import { provideTransloco } from '@jsverse/transloco';
import { AppTranslocoLoader } from './i18n/transloco-loader';
import { LocaleService } from './i18n/locale.service';


export const appConfig: ApplicationConfig = {
  providers: [
    // Core Angular providers
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([authInterceptor]), withFetch()),
    provideTransloco({
      config: {
        availableLangs: ['en', 'ar', 'ur'],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          allowEmpty: false,
          useFallbackTranslation: true,
          logMissingKey: true
        }
      },
      loader: AppTranslocoLoader
    }),
    provideAppInitializer(() => inject(LocaleService).initialize()),
    importProvidersFrom(
      NgxGoogleAnalyticsModule.forRoot(environment.firebaseConfig.measurementId),
      NgxGoogleAnalyticsRouterModule.forRoot()
    ),

    // PrimeNG services and theme preset
    MessageService,
    ConfirmationService,
    DialogService,
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.app-dark',
          ripple: true,
        }
      }
    }),

    // Modern, tree-shakable Firebase providers
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    provideStorage(() => getStorage()),
    provideDatabase(() => getDatabase()),
    provideFirestore(() => getFirestore()),
    provideFunctions(() => getFunctions()),
    provideMessaging(() => getMessaging()),


    // Application services
    CustomerService,
    BaseMasterDataService,
    { provide: ErrorHandler, useClass: StaleBuildErrorHandler },

    // Service Worker
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
  ]
};
